/**
 * Automatisch stempeln am Ladengerät (ab 2.5.0): Ist dieses Gerät als Ladenkasse markiert (Schalter unter
 * Einstellungen → Zeiterfassung, nur in diesem Browser gespeichert), stempelt die Anmeldung „Kommen“ und die Abmeldung
 * „Gehen“ – über die normalen Endpunkte /timeclock/clock-in und /clock-out (gleiche Prüfungen, gleiches Protokoll).
 * Gilt für Mitarbeiter mit dem Recht „Eigene Arbeitszeit stempeln“, nicht für Administratoren (Inhaber) und nicht, solange
 * das Passwort noch geändert werden muss. Fehler stören die An-/Abmeldung nie; ohne Verbindung wird nichts gestempelt.
 */
import { api, ApiError } from '../../lib/api';
import type { User } from '../../lib/types';
import { autoClockApplies } from './autoClockRule';
import { localTime } from './util';

const DEVICE_KEY = 'kk_auto_clock_device';
const FLASH_KEY = 'kk_clock_flash';

export function isAutoClockDevice(): boolean {
  try { return localStorage.getItem(DEVICE_KEY) === '1'; } catch { return false; }
}

export function setAutoClockDevice(on: boolean): void {
  try {
    if (on) localStorage.setItem(DEVICE_KEY, '1');
    else localStorage.removeItem(DEVICE_KEY);
  } catch { /* Speicher nicht verfügbar */ }
}

interface ClockState { serverTime: number; workStartedAt: number | null }

const sinceHint = 'Du warst noch vom letzten Mal eingestempelt – bitte unter „Kommen/Gehen“ eintragen, wann du gegangen bist.';

/** Nach der Anmeldung: „Kommen“ stempeln. Ergebnis = Meldung für die Anzeige (oder null). */
export async function autoClockIn(user: User): Promise<{ text: string; tone: 'success' | 'info' } | null> {
  if (!autoClockApplies(user, isAutoClockDevice())) return null;
  try {
    const s = await api.post<ClockState>('/timeclock/clock-in', {});
    return { text: `Kommen gestempelt um ${localTime(s.workStartedAt ?? s.serverTime)} Uhr.`, tone: 'success' };
  } catch (e) {
    if (e instanceof ApiError && e.code === 'FORGOT_CLOCK_OUT') return { text: sinceHint, tone: 'info' };
    return null; // schon eingestempelt, keine Verbindung … – die Anmeldung geht trotzdem weiter
  }
}

/** Vor der Abmeldung: „Gehen“ stempeln. Die Meldung zeigt die Anmeldeseite danach an (takeClockFlash). */
export async function autoClockOut(user: User): Promise<void> {
  if (!autoClockApplies({ ...user, mustChangePassword: false }, isAutoClockDevice())) return;
  let text: string | null = null;
  try {
    const s = await api.post<ClockState>('/timeclock/clock-out', {});
    text = `${user.displayName}: Gehen gestempelt um ${localTime(s.serverTime)} Uhr.`;
  } catch (e) {
    if (e instanceof ApiError && e.code === 'FORGOT_CLOCK_OUT') text = `${user.displayName}: ${sinceHint}`;
    else if (e instanceof ApiError && e.code === 'NETWORK') text = `${user.displayName}: Ohne Verbindung – „Gehen“ wurde nicht gestempelt. Bitte nachtragen lassen.`;
  }
  if (text) {
    try { sessionStorage.setItem(FLASH_KEY, text); } catch { /* egal */ }
  }
}

/** Meldung der letzten automatischen Abmeldung (einmalig) – für die Anmeldeseite. */
export function takeClockFlash(): string | null {
  try {
    const t = sessionStorage.getItem(FLASH_KEY);
    if (t) sessionStorage.removeItem(FLASH_KEY);
    return t;
  } catch {
    return null;
  }
}
